/**
 * Parses the small mark-up used in constants/helpTexts.ts, so HelpTip can
 * render it without v-html:
 * - `**bold**` and `_italic_` inline
 * - a line break starts a new line; a blank line starts a new paragraph
 * - lines starting with `- ` form a bullet list
 */
export type HelpSpan = { text: string; bold: boolean; italic: boolean };
export type HelpLine = HelpSpan[];
export type HelpBlock =
  | { type: 'paragraph'; lines: HelpLine[] }
  | { type: 'list'; items: HelpLine[] };

const parseInline = (line: string): HelpLine => {
  const spans: HelpSpan[] = [];
  let bold = false;
  let italic = false;
  let text = '';

  const flush = () => {
    if (text) spans.push({ text, bold, italic });
    text = '';
  };

  for (let i = 0; i < line.length; i++) {
    if (line.startsWith('**', i)) {
      flush();
      bold = !bold;
      i++;
    } else if (line[i] === '_') {
      flush();
      italic = !italic;
    } else {
      text += line[i];
    }
  }
  flush();
  return spans;
};

export const parseHelpText = (source: string): HelpBlock[] => {
  const blocks: HelpBlock[] = [];

  for (const paragraph of source.split(/\n\s*\n/)) {
    let current: HelpBlock | undefined;

    for (const line of paragraph.split('\n')) {
      const isItem = line.startsWith('- ');
      const type = isItem ? 'list' : 'paragraph';
      if (current?.type !== type) {
        current =
          type === 'list'
            ? { type, items: [] }
            : { type: 'paragraph', lines: [] };
        blocks.push(current);
      }
      const parsed = parseInline(isItem ? line.slice(2) : line);
      if (current.type === 'list') current.items.push(parsed);
      else current.lines.push(parsed);
    }
  }
  return blocks;
};
